import { atom } from "jotai";
import {
  acquisitionInvestorsPercentCommonEquityAtom,
  acquisitionInvestorsPercentPreferredEquityEquityMethodAtom,
  preStepUpEquityPercentAcquisitionInvestorsAtom,
  preStepUpEquityPercentSearchPhaseInvestorsAtom,
  preStepUpEquityPercentSearcherCoInvestAtom,
  preStepUpEquityPercentSellerRolloverAtom,
  promoteSearchersCarryPercentCommonEquityAtom,
  searchPhaseInvestorsPercentCommonEquityAtom,
  searchPhaseInvestorsPercentPreferredEquityEquityMethodAtom,
  searcherCoInvestPercentCommonEquityAtom,
  sellerRolloverPercentCommonEquityAtom,
  sellerRolloverPercentPreferredEquityEquityMethodAtom,
  totalCapitalInvestedAtom,
  totalFinanceCapitalInvestedAtom,
} from "./companyOwnership";
import {
  companySaleIncludedAtom,
  companySaleYearAtom,
  equityInvestorPreferredEquityRateAtom,
  equityInvestorStartPreferredDividendsAtom,
  equityInvestorStartPrincipalRepaymentsAtom,
  equityInvestorStartProfitDistributionsAtom,
  isExternalEquityAtom,
} from "./general";
import { createComputedIterationMolecule } from "./iterations";
import {
  companySaleDebtPayoffMolecule,
  incomeTaxesMolecule,
  proceedsFromCompanySaleMolecule,
  totalDebtServiceMolecule,
  unleveredFreeCashFlowExCompanySaleMolecule,
} from "./projections";

export const outsidePreferredEquityPercentAtom = atom((get) => {
  const E73 = get(searchPhaseInvestorsPercentPreferredEquityEquityMethodAtom); //Search Phase Investors - Preferred Equity - Equity Method
  const E74 = get(acquisitionInvestorsPercentPreferredEquityEquityMethodAtom); //Acquisition Investors - Preferred Equity - Equity Method
  const E76 = get(sellerRolloverPercentPreferredEquityEquityMethodAtom); //Seller Equity Rollover - Preferred Equity - Equity Method

  return E73 + E74 + E76;
});

////////
// Distributable FCF
////////

export const waterfallDistributableFCFFromOpsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const unleveredFreeCashFlowMoleculeExCompanySale =
        Number(get(get(unleveredFreeCashFlowExCompanySaleMolecule)[i]).value) ||
        0;

      const totalDebtService =
        Number(get(get(totalDebtServiceMolecule)[i]).value) || 0;

      return {
        isLocked: true,
        type: "dollar",
        value: unleveredFreeCashFlowMoleculeExCompanySale - totalDebtService,
      };
    });
  });

export const waterfallDistributableFCFFromCoSaleMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const proceedsFromCompanySale = get(
        get(proceedsFromCompanySaleMolecule)[i],
      );

      const companyPayoff = Number(
        get(get(companySaleDebtPayoffMolecule)[i]).value,
      );

      return {
        isLocked: true,
        type: "dollar",
        value: Number(proceedsFromCompanySale.value) - companyPayoff,
      };
    });
  });

////////
// Preferred Dividends
////////

export const waterfallPreferredDividendsRequiredMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      return {
        isLocked: true,
        type: "dollar",
        value: Number(
          get(waterfallPaymentsAtom)?.[i]?.preferredDividendsRequired,
        ),
      }; // Access the computed atom to trigger recalculation
    });
  });

export const waterfallPreferredDividendsPaidMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      return {
        isLocked: true,
        type: "dollar",
        value: Number(get(waterfallPaymentsAtom)?.[i]?.preferredDividendsPaid),
      }; // Access the computed atom to trigger recalculation
    });
  });

export const waterfallPaymentsAtom = atom((get) => {
  //   const isEquityCalculationMethodUsingProjectCost = get(
  //     isEquityCalculationMethodUsingProjectCostAtom,
  //   );

  const waterfallDistributableFCFFromCoSaleAtoms = get(
    waterfallDistributableFCFFromCoSaleMolecule,
  );

  const rows: {
    preferredDividendsBeginBal: number;
    preferredDividendsEndBal: number;
    preferredDividendsPymtFromOps: number;
    preferredDividendsPymtFromCoSale: number;

    principalRepaymentBeginBal: number;
    principalRepaymentEndBal: number;
    principalRepaymentPymtFromOps: number;
    principalRepaymentPymtFromCoSale: number;

    preferredDividendsRequired: number;
    preferredDividendsPaid: number;
    preferredDividendsAccrued: number;
  }[] = [];

  for (let i = 0; i < waterfallDistributableFCFFromCoSaleAtoms.length; i++) {
    const isInitialYear = i <= 2;
    const prevRow = i > 0 ? rows[i - 1] : null;
    const companySaleYear = get(companySaleIncludedAtom)
      ? Number(get(companySaleYearAtom))
      : NaN; //year of Company Sale

    const currentYear = i - 1; // current year
    const Assumptions_D51 = Number(get(equityInvestorPreferredEquityRateAtom)); // Equity Investor Terms - Investor's Preferred Equity Rate (%)
    const AP121 =
      Number(get(get(waterfallDistributableFCFFromOpsMolecule)[i]).value) || 0; // distributable FCF from Ops.
    const Assumptions_D53 = Number(
      get(equityInvestorStartPreferredDividendsAtom),
    ); //Year to Start Preferred Dividends

    // principalRepaymentBeginBal beginBal
    let principalRepaymentBeginBal = 0;
    if (isInitialYear) {
      const Assumptions_D43 = get(isExternalEquityAtom); //Do you have Equity Investors?
      const Assumptions_D66 = get(totalFinanceCapitalInvestedAtom); // Total Preferred Cap. Invested ($)

      if (Assumptions_D43) {
        principalRepaymentBeginBal = Assumptions_D66 > 0 ? Assumptions_D66 : 0;
      } else {
        principalRepaymentBeginBal = 0;
      }
    } else {
      const BF121 = Number(prevRow?.principalRepaymentEndBal); // ending capital balance
      principalRepaymentBeginBal = currentYear > companySaleYear ? 0 : BF121;
    }

    // payment from Ops.
    //const currentYear = i - 2; // current year
    const BC121 = principalRepaymentBeginBal; // Principal Repayment - Capital (Begin Bal.)

    const preferredDividendsRequired =
      currentYear > companySaleYear ? 0 : BC121 * Assumptions_D51;

    const preferredDividendsPaid = Math.max(
      0,
      Assumptions_D53 > currentYear
        ? 0
        : Math.min(preferredDividendsRequired, AP121),
    );

    const AS121 = Number(preferredDividendsPaid) || 0; // preferred dividends paid

    // const AS121 = Number(
    //   get(get(waterfallPreferredDividendsPaidMolecule)[i]).value,
    // );

    const preferredDividendsAccrued = preferredDividendsRequired - AS121;

    // beginBal
    let preferredDividendsBeginBal = Number(preferredDividendsAccrued) || 0;
    if (!isInitialYear) {
      const AT122 = Number(preferredDividendsAccrued) || 0;
      const AX121 = Number(prevRow?.preferredDividendsEndBal);
      preferredDividendsBeginBal = AT122 + AX121;
    }

    // pymtFromOps
    const AU121 = preferredDividendsBeginBal; // Accrued Pref. (Begin Bal.)

    const preferredDividendsPymtFromOps =
      Assumptions_D53 > currentYear
        ? 0
        : Math.min(AU121, Math.max(0, AP121 - AS121));

    const AU130 = preferredDividendsBeginBal; // Accrued Pref. (Begin Bal.)
    const AV130 = preferredDividendsPymtFromOps; // preferred dividends - Accrued Pref. (Pymt from Ops.)
    const AQ130 =
      Number(get(get(waterfallDistributableFCFFromCoSaleMolecule)[i]).value) ||
      0; //distributable FCF from Co. Sale
    const preferredDividendsPymtFromCoSale = Math.min(AU130 - AV130, AQ130);

    // endBal
    const preferredDividendsEndBal =
      preferredDividendsBeginBal -
      preferredDividendsPymtFromOps -
      preferredDividendsPymtFromCoSale;

    // Principal Repayment

    // const AP121 =
    //   Number(get(get(waterfallDistributableFCFFromOpsMolecule)[i]).value) || 0; // distributable FCF from Ops.
    // const AS121 =
    //   Number(get(get(waterfallPreferredDividendsPaidMolecule)[i]).value) || 0; // preferred dividends paid

    const AV121 = Number(preferredDividendsPymtFromOps) || 0; // preferred dividends Accrued Pref. (Pymt from Ops.)
    const Assumptions_D54 = Number(
      get(equityInvestorStartPrincipalRepaymentsAtom),
    ); //Year to Start Principal Repayments

    const principalRepaymentPymtFromOps =
      Assumptions_D54 > currentYear
        ? 0
        : Math.min(BC121, Math.max(0, AP121 - AS121 - AV121));

    // pymtFromCoSale
    //const BC121 = 0;
    const BD121 = principalRepaymentPymtFromOps;
    const AQ121 =
      Number(get(get(waterfallDistributableFCFFromCoSaleMolecule)[i]).value) ||
      0; //distributable FCF from Co. Sale
    const AW121 = Number(preferredDividendsPymtFromCoSale) || 0; // preferred dividends - Accrued Pref. (Pymt @ Co. Sale)

    const principalRepaymentPymtFromCoSale = Math.min(
      BC121 - BD121,
      AQ121 - AW121,
    );

    // endBal
    //const BC121 = 0;
    //const BD121 = 0;
    const BE121 = principalRepaymentPymtFromCoSale;

    const principalRepaymentEndBal = BC121 - BD121 - BE121;

    rows.push({
      principalRepaymentBeginBal,
      principalRepaymentPymtFromOps,
      principalRepaymentPymtFromCoSale,
      principalRepaymentEndBal,
      preferredDividendsBeginBal,
      preferredDividendsEndBal,
      preferredDividendsPymtFromOps,
      preferredDividendsRequired,
      preferredDividendsPymtFromCoSale,
      preferredDividendsAccrued,
      preferredDividendsPaid,
    });
  }

  return rows;
});

export const waterfallPreferredDividendsAccruedPrefBeginBalMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      return {
        isLocked: true,
        type: "dollar",
        value: Number(
          get(waterfallPaymentsAtom)?.[i]?.preferredDividendsBeginBal,
        ),
      }; // Access the computed atom to trigger recalculation
    });
  });

export const waterfallPreferredDividendsAccruedPrefPymtFromOpsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      return {
        isLocked: true,
        type: "dollar",
        value: Number(
          get(waterfallPaymentsAtom)?.[i]?.preferredDividendsPymtFromOps,
        ),
      }; // Access the computed atom to trigger recalculation
    });
  });

export const waterfallPreferredDividendsAccruedPrefPymtFromCoSaleMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      return {
        isLocked: true,
        type: "dollar",
        value: Number(
          get(waterfallPaymentsAtom)?.[i]?.preferredDividendsPymtFromCoSale,
        ),
      }; // Access the computed atom to trigger recalculation
    });
  });

export const waterfallPreferredDividendsAccruedPrefEndBalMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      return {
        isLocked: true,
        type: "dollar",
        value: Number(
          get(waterfallPaymentsAtom)?.[i]?.preferredDividendsEndBal,
        ),
      }; // Access the computed atom to trigger recalculation
    });
  });

export const waterfallPrincipalRepaymentCapitalBeginBalMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const beginBal =
        get(waterfallPaymentsAtom)?.[i]?.principalRepaymentBeginBal || 0;

      return {
        isLocked: true,
        type: "dollar",
        value: beginBal,
      };
    });
  });

export const waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const pymtFromOps =
        get(waterfallPaymentsAtom)?.[i]?.principalRepaymentPymtFromOps || 0;

      return {
        isLocked: true,
        type: "dollar",
        value: pymtFromOps,
      };
    });
  });

export const waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const pymtFromCoSale =
        get(waterfallPaymentsAtom)?.[i]?.principalRepaymentPymtFromCoSale || 0;

      return {
        isLocked: true,
        type: "dollar",
        value: pymtFromCoSale,
      };
    });
  });

export const waterfallPrincipalRepaymentCapitalEndBalMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const endBal =
        get(waterfallPaymentsAtom)?.[i]?.principalRepaymentEndBal || 0;

      return {
        isLocked: true,
        type: "dollar",
        value: endBal,
      };
    });
  });

export const waterfallPreferredDividendsAccruedMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const preferredDividendsAccrued =
        get(waterfallPaymentsAtom)?.[i]?.preferredDividendsAccrued || 0;

      return {
        isLocked: true,
        type: "dollar",
        value: preferredDividendsAccrued,
      };
    });
  });

export const waterfallPreferredDividendsSearchPhaseInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const AS137 = Number(
        get(get(waterfallPreferredDividendsPaidMolecule)[i]).value || 0, // preferred dividends paid
      );
      const AV137 = Number(
        get(get(waterfallPreferredDividendsAccruedPrefPymtFromOpsMolecule)[i])
          .value || 0,
      );
      const AW137 = Number(
        get(
          get(waterfallPreferredDividendsAccruedPrefPymtFromCoSaleMolecule)[i],
        ).value || 0,
      );

      const Assumptions_E62 = get(
        preStepUpEquityPercentSearchPhaseInvestorsAtom,
      );

      const value = (AS137 + (AV137 + AW137)) * Assumptions_E62;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPreferredDividendsAquisitionInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const AS121 = Number(
        get(get(waterfallPreferredDividendsPaidMolecule)[i]).value || 0, // preferred dividends paid
      );
      const AV121 = Number(
        get(get(waterfallPreferredDividendsAccruedPrefPymtFromOpsMolecule)[i])
          .value || 0,
      );
      const AW121 = Number(
        get(
          get(waterfallPreferredDividendsAccruedPrefPymtFromCoSaleMolecule)[i],
        ).value || 0,
      );
      const Assumptions_E63 = get(
        preStepUpEquityPercentAcquisitionInvestorsAtom,
      );

      const value = (AS121 + (AV121 + AW121)) * Assumptions_E63;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPreferredDividendsSearchersCoinvestMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const AS121 = Number(
        get(get(waterfallPreferredDividendsPaidMolecule)[i]).value || 0, // preferred dividends paid
      );
      const AV121 = Number(
        get(get(waterfallPreferredDividendsAccruedPrefPymtFromOpsMolecule)[i])
          .value || 0,
      );
      const AW121 = Number(
        get(
          get(waterfallPreferredDividendsAccruedPrefPymtFromCoSaleMolecule)[i],
        ).value || 0,
      );
      const Assumptions_E64 = get(preStepUpEquityPercentSearcherCoInvestAtom);

      const value = (AS121 + (AV121 + AW121)) * Assumptions_E64;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPreferredDividendsSellerRolloverMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const AS121 = Number(
        get(get(waterfallPreferredDividendsPaidMolecule)[i]).value || 0, // preferred dividends paid
      );
      const AV121 = Number(
        get(get(waterfallPreferredDividendsAccruedPrefPymtFromOpsMolecule)[i])
          .value || 0,
      );
      const AW121 = Number(
        get(
          get(waterfallPreferredDividendsAccruedPrefPymtFromCoSaleMolecule)[i],
        ).value || 0,
      );
      const Assumptions_E65 = get(preStepUpEquityPercentSellerRolloverAtom);

      const value = (AS121 + (AV121 + AW121)) * Assumptions_E65;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

const nullAtom = atom(null);

export const waterfallPrincipalRepaymentCumulativePrincipalRepaidMolecule =
  createComputedIterationMolecule((i, _iter, prevAtoms) => {
    return atom((get) => {
      if (i < 2) {
        return {
          isLocked: true,
          type: "dollar",
          value: 0,
        };
      }

      const prevAtom = prevAtoms[i - 1];
      const prevValue = prevAtom ? Number(get(prevAtom).value || 0) : 0;

      const currentYear = i - 2; // current year

      const BD121 = Number(
        get(
          get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[i] ||
            nullAtom,
        )?.value || 0,
      );

      const BE121 = Number(
        get(
          get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[i] ||
            nullAtom,
        )?.value || 0,
      );

      const companySaleYear = Number(get(companySaleYearAtom)); //year of Company Sale
      if (currentYear > companySaleYear) {
        return {
          isLocked: true,
          type: "dollar",
          value: null,
        };
      }

      const value = prevValue + BD121 + BE121;

      const Assumptions_D66 = Number(get(totalCapitalInvestedAtom)); // Total Preferred Cap. Invested ($)

      if (
        Math.round(prevValue) === Math.round(Assumptions_D66) ||
        get(prevAtoms[i - 1]).value === null
      ) {
        return {
          isLocked: true,
          type: "dollar",
          value: null, //"Principal Returned"
        };
      }

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPrincipalRepaymentSearchPhaseInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BD121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[i])
          .value || 0,
      );
      const BE121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E62 = get(
        preStepUpEquityPercentSearchPhaseInvestorsAtom,
      );

      const value = (BD121 + BE121) * Assumptions_E62;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPrincipalRepaymentAquisitionInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BD121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[i])
          .value || 0,
      );
      const BE121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E63 = get(
        preStepUpEquityPercentAcquisitionInvestorsAtom,
      );

      const value = (BD121 + BE121) * Assumptions_E63;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPrincipalRepaymentSearchersCoinvestMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BD121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[i])
          .value || 0,
      );
      const BE121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E64 = get(preStepUpEquityPercentSearcherCoInvestAtom);

      const value = (BD121 + BE121) * Assumptions_E64;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallPrincipalRepaymentSellerRolloverMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BD121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[i])
          .value || 0,
      );
      const BE121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E65 = get(preStepUpEquityPercentSellerRolloverAtom);

      const value = (BD121 + BE121) * Assumptions_E65;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallAmountForProfitDistributionsFromOpsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      //current year
      const AO137 = i - 1; // current year
      // distributable fcf from Ops.
      const AP121 = Number(
        get(get(waterfallDistributableFCFFromOpsMolecule)[i]).value || 0,
      );
      // preferred dividends paid
      const AS121 = Number(
        get(get(waterfallPreferredDividendsPaidMolecule)[i]).value || 0,
      );

      // preferred dividends accrued pymt from Ops.
      const AV121 = Number(
        get(get(waterfallPreferredDividendsAccruedPrefPymtFromOpsMolecule)[i])
          .value || 0,
      );

      // preferred dividends pymt captial pymt from ops
      const BD121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E82 = get(searcherCoInvestPercentCommonEquityAtom);
      const projectionsAP128 = Number(
        get(get(waterfallDistributableFCFFromOpsMolecule)[i]).value || 0,
      );

      let value = Math.max(0, AP121 - AS121 - AV121 - BD121);

      const assumptionsD55 =
        Number(get(equityInvestorStartProfitDistributionsAtom)) || 0;

      if (assumptionsD55 > AO137) value = 0;

      if (Assumptions_E82 === 1 || Assumptions_E82 === 100) {
        value = projectionsAP128;
      }

      const sumBD = (() => {
        let total = 0;
        for (let j = 2; j <= i; j++) {
          total += Number(
            get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[j])
              .value,
          );
        }
        return total;
      })();

      const sumBE = (() => {
        let total = 0;
        for (let j = 2; j <= i; j++) {
          total += Number(
            get(
              get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[j],
            ).value,
          );
        }
        return total;
      })();

      const assumptionsD66 = Number(get(totalFinanceCapitalInvestedAtom)) || 0;

      if (
        sumBD + sumBE > 0 &&
        sumBD + sumBE <
          assumptionsD66 - 0.00001 /* to account for floating point precision */
      ) {
        value = 0;
      }

      if (value < 0) value = 0;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallAmountForProfitDistributionsFromCoSaleMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const AQ121 = Number(
        get(get(waterfallDistributableFCFFromCoSaleMolecule)[i]).value || 0,
      );

      const AW121 = Number(
        get(
          get(waterfallPreferredDividendsAccruedPrefPymtFromCoSaleMolecule)[i],
        ).value || 0,
      );
      const BE121 = Number(
        get(get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[i])
          .value || 0,
      );
      const Assumptions_E82 = get(searcherCoInvestPercentCommonEquityAtom);
      const projectionsAP128 = Number(
        get(get(waterfallDistributableFCFFromCoSaleMolecule)[i]).value || 0,
      );

      const sumBD = (() => {
        let total = 0;
        for (let j = 2; j <= i; j++) {
          total += Number(
            get(get(waterfallPrincipalRepaymentCapitalPymtFromOpsMolecule)[j])
              .value,
          );
        }
        return total;
      })();

      const sumBE = (() => {
        let total = 0;
        for (let j = 2; j <= i; j++) {
          total += Number(
            get(
              get(waterfallPrincipalRepaymentCapitalPymtFromCoSaleMolecule)[j],
            ).value,
          );
        }
        return total;
      })();

      const assumptionsD66 =
        Number(get(totalFinanceCapitalInvestedAtom) - 0.00001) || 0;

      const value =
        Assumptions_E82 === 1
          ? projectionsAP128
          : sumBD + sumBE < assumptionsD66 && sumBD + sumBE > 0
            ? 0
            : Math.max(0, AQ121 - AW121 - BE121);

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallProfitDistributionsSearchPhaseInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BL121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E80 = get(searchPhaseInvestorsPercentCommonEquityAtom);

      const value = BL121 * Assumptions_E80;

      // if equityInvestorStartProfitDistributionsAtom > current year then 0
      if (i <= Number(get(equityInvestorStartProfitDistributionsAtom))) {
        return {
          isLocked: true,
          type: "dollar",
          value: 0,
        };
      }

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallProfitDistributionsAcquisitionInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BL121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E81 = get(acquisitionInvestorsPercentCommonEquityAtom);

      const value = BL121 * Assumptions_E81;

      if (i <= Number(get(equityInvestorStartProfitDistributionsAtom))) {
        return {
          isLocked: true,
          type: "dollar",
          value: 0,
        };
      }

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallProfitDistributionsSearchersCoinvestMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BL121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E82 = get(searcherCoInvestPercentCommonEquityAtom);

      const value = BL121 * Assumptions_E82;

      if (i <= Number(get(equityInvestorStartProfitDistributionsAtom))) {
        return {
          isLocked: true,
          type: "dollar",
          value: 0,
        };
      }

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallProfitDistributionsSellerRolloverMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BL121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E83 = get(sellerRolloverPercentCommonEquityAtom);

      const value = BL121 * Assumptions_E83;

      if (i <= Number(get(equityInvestorStartProfitDistributionsAtom))) {
        return {
          isLocked: true,
          type: "dollar",
          value: 0,
        };
      }

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallProfitDistributionsPromoteSearchersCarryMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BL121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromOpsMolecule)[i])
          .value || 0,
      );

      const Assumptions_E84 = get(promoteSearchersCarryPercentCommonEquityAtom);

      const value = BL121 * Number(Assumptions_E84);

      if (i <= Number(get(equityInvestorStartProfitDistributionsAtom))) {
        return {
          isLocked: true,
          type: "dollar",
          value: 0,
        };
      }

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallcompanySaleSearchPhaseInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BM121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromCoSaleMolecule)[i])
          .value || 0,
      );

      const Assumptions_E80 = get(searchPhaseInvestorsPercentCommonEquityAtom);

      const value = BM121 * Assumptions_E80;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallcompanySaleAcquisitionInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BM121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromCoSaleMolecule)[i])
          .value || 0,
      );

      const Assumptions_E81 = get(acquisitionInvestorsPercentCommonEquityAtom);

      const value = BM121 * Assumptions_E81;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallcompanySaleSearchersCoinvestMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BM121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromCoSaleMolecule)[i])
          .value || 0,
      );

      const Assumptions_E82 = get(searcherCoInvestPercentCommonEquityAtom);

      const value = BM121 * Assumptions_E82;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallcompanySaleSellerRolloverMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BM121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromCoSaleMolecule)[i])
          .value || 0,
      );

      const Assumptions_E83 = get(sellerRolloverPercentCommonEquityAtom);

      const value = BM121 * Assumptions_E83;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallcompanySalePromoteSearchersCarryMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const BM121 = Number(
        get(get(waterfallAmountForProfitDistributionsFromCoSaleMolecule)[i])
          .value || 0,
      );

      const Assumptions_E84 = get(promoteSearchersCarryPercentCommonEquityAtom);

      const value = BM121 * Number(Assumptions_E84);

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallTaxDistributionsSearchPhaseInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const J49 = Number(get(get(incomeTaxesMolecule)[i]).value || 0);

      const Assumptions_E80 = get(searchPhaseInvestorsPercentCommonEquityAtom);

      const value = J49 * Assumptions_E80;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallTaxDistributionsAcquisitionInvestorsMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const J49 = Number(get(get(incomeTaxesMolecule)[i]).value || 0);

      const Assumptions_E81 = get(acquisitionInvestorsPercentCommonEquityAtom);

      const value = J49 * Assumptions_E81;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallTaxDistributionsSearchersCoinvestMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const J49 = Number(get(get(incomeTaxesMolecule)[i]).value || 0);

      const Assumptions_E82 = get(searcherCoInvestPercentCommonEquityAtom);

      const value = J49 * Assumptions_E82;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallTaxDistributionsSellerRolloverMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const J49 = Number(get(get(incomeTaxesMolecule)[i]).value || 0);

      const Assumptions_E83 = get(sellerRolloverPercentCommonEquityAtom);

      const value = J49 * Assumptions_E83;

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

export const waterfallTaxDistributionsPromoteSearchersCarryMolecule =
  createComputedIterationMolecule((i) => {
    return atom((get) => {
      const J49 = Number(get(get(incomeTaxesMolecule)[i]).value || 0);

      const Assumptions_E84 = get(promoteSearchersCarryPercentCommonEquityAtom);

      const value = J49 * Number(Assumptions_E84);

      return {
        isLocked: true,
        type: "dollar",
        value,
      };
    });
  });

// HELPER TABLE [5]
// const preferredEquitySearchPhaseCapitalSplitProjectCostMethod = atom((get) => {
//   return (
//     get(preStepUpEquityPercentSearchPhaseInvestorsAtom) /
//     get(preferredEquityTotalPercentAtom)
//   );
// });

// const preferredEquityAcquisitionInvestorsCapitalSplitProjectCostMethod = atom(
//   (get) => {
//     return (
//       get(preStepUpEquityPercentAcquisitionInvestorsAtom) /
//       get(preferredEquityTotalPercentAtom)
//     );
//   },
// );

// const preferredEquitySearchersCoInvestCapitalSplitProjectCostMethod = atom(
//   (get) => {
//     return (
//       get(preStepUpEquityPercentSearcherCoInvestAtom) /
//       get(preferredEquityTotalPercentAtom)
//     );
//   },
// );

// const preferredEquitySellerRolloverCapitalSplitProjectCostMethod = atom(
//   (get) => {
//     return (
//       get(preStepUpEquityPercentSellerRolloverAtom) /
//       get(preferredEquityTotalPercentAtom)
//     );
//   },
// );
